"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { niceTicks } from "@/components/tools/stats-tests/interaction-plot";
import { downloadSvg } from "@/lib/tool-kit/download";
import { CHART_INK, seriesColor } from "@/lib/tool-kit/palette";
import type { NumberFormatter } from "@/lib/tool-kit/stats-tests/format";

/*
 * Empirical CDF step plots for the Kolmogorov–Smirnov calculator: one or two ECDFs, an
 * optional smooth reference CDF, and a vertical marker at the largest gap D. Colours are
 * hex (the Okabe–Ito palette) because the exported SVG carries no stylesheet.
 */

const WIDTH = 560;
const HEIGHT = 380;
const MARGIN = { top: 24, right: 24, bottom: 84, left: 64 };
const PLOT_W = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = HEIGHT - MARGIN.top - MARGIN.bottom;
const CURVE_STEPS = 120;

export interface EcdfSeries {
  name: string;
  /** Sorted values. */
  values: readonly number[];
}

export interface GapMarker {
  x: number;
  /** ECDF or CDF height at each end of the gap. */
  low: number;
  high: number;
  label: string;
}

/** Path of a right-continuous step ECDF across [xMin, xMax]. */
function stepPath(
  values: readonly number[],
  xMin: number,
  xMax: number,
  x: (v: number) => number,
  y: (v: number) => number
): string {
  const n = values.length;
  const parts = [`M${x(xMin)},${y(0)}`];
  for (const [index, value] of values.entries()) {
    parts.push(`H${x(value)}`, `V${y((index + 1) / n)}`);
  }
  parts.push(`H${x(xMax)}`);
  return parts.join(" ");
}

export function EcdfPlot({
  series,
  curve,
  curveName,
  gap,
  xTitle,
  yTitle,
  label,
  downloadLabel,
  fileName,
  fmt,
}: {
  series: readonly EcdfSeries[];
  /** A smooth reference CDF, sampled across the plotted range. */
  curve: ((value: number) => number) | null;
  curveName: string;
  gap: GapMarker;
  xTitle: string;
  yTitle: string;
  label: string;
  downloadLabel: string;
  fileName: string;
  fmt: NumberFormatter;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const all = series.flatMap((s) => [...s.values]);
  const xTicks = niceTicks(Math.min(...all), Math.max(...all));
  const xMin = xTicks[0];
  const xMax = xTicks.at(-1) as number;
  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const x = (v: number) =>
    MARGIN.left + ((v - xMin) / (xMax - xMin || 1)) * PLOT_W;
  const y = (v: number) => MARGIN.top + PLOT_H - v * PLOT_H;
  const curvePoints = curve
    ? Array.from({ length: CURVE_STEPS + 1 }, (_, i) => {
        const value = xMin + ((xMax - xMin) * i) / CURVE_STEPS;
        return `${x(value)},${y(curve(value))}`;
      }).join(" ")
    : "";
  const legend = [
    ...series.map((s, i) => ({ name: s.name, color: seriesColor(i) })),
    ...(curve ? [{ name: curveName, color: seriesColor(3) }] : []),
  ];
  return (
    <div className="space-y-2">
      <svg
        aria-label={label}
        className="h-auto w-full max-w-xl rounded-lg border border-border"
        ref={ref}
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
        {yTicks.map((tick) => (
          <g key={`y-${tick}`}>
            <line
              stroke={CHART_INK.grid}
              x1={MARGIN.left}
              x2={MARGIN.left + PLOT_W}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              dominantBaseline="middle"
              fill={CHART_INK.muted}
              fontSize="12"
              textAnchor="end"
              x={MARGIN.left - 8}
              y={y(tick)}
            >
              {fmt(tick)}
            </text>
          </g>
        ))}
        {xTicks.map((tick) => (
          <g key={`x-${tick}`}>
            <line
              stroke={CHART_INK.grid}
              x1={x(tick)}
              x2={x(tick)}
              y1={MARGIN.top}
              y2={MARGIN.top + PLOT_H}
            />
            <text
              fill={CHART_INK.muted}
              fontSize="12"
              textAnchor="middle"
              x={x(tick)}
              y={MARGIN.top + PLOT_H + 18}
            >
              {fmt(tick)}
            </text>
          </g>
        ))}
        {curve ? (
          <polyline
            fill="none"
            points={curvePoints}
            stroke={seriesColor(3)}
            strokeDasharray="6 4"
            strokeWidth="2"
          />
        ) : null}
        {series.map((s, i) => (
          <path
            d={stepPath(s.values, xMin, xMax, x, y)}
            fill="none"
            key={s.name}
            stroke={seriesColor(i)}
            strokeWidth="2"
          />
        ))}
        <line
          stroke={CHART_INK.text}
          strokeWidth="2"
          x1={x(gap.x)}
          x2={x(gap.x)}
          y1={y(gap.low)}
          y2={y(gap.high)}
        />
        <text
          fill={CHART_INK.text}
          fontSize="12"
          fontWeight="600"
          textAnchor="start"
          x={x(gap.x) + 6}
          y={y((gap.low + gap.high) / 2)}
        >
          {gap.label}
        </text>
        <text
          fill={CHART_INK.text}
          fontSize="13"
          textAnchor="middle"
          x={MARGIN.left + PLOT_W / 2}
          y={MARGIN.top + PLOT_H + 40}
        >
          {xTitle}
        </text>
        <text
          fill={CHART_INK.text}
          fontSize="13"
          textAnchor="middle"
          transform={`translate(18 ${MARGIN.top + PLOT_H / 2}) rotate(-90)`}
        >
          {yTitle}
        </text>
        {legend.map((item, i) => (
          <g key={item.name}>
            <line
              stroke={item.color}
              strokeWidth="3"
              x1={MARGIN.left + i * 150}
              x2={MARGIN.left + i * 150 + 22}
              y1={HEIGHT - 20}
              y2={HEIGHT - 20}
            />
            <text
              dominantBaseline="middle"
              fill={CHART_INK.text}
              fontSize="12"
              x={MARGIN.left + i * 150 + 28}
              y={HEIGHT - 20}
            >
              {item.name}
            </text>
          </g>
        ))}
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadSvg(fileName, ref.current);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
